import Axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { HttpMethod, IRequestParams } from './models';
import { RestApiError } from '../utils';
import axios from 'axios';
import { captureException, setContext, setUser } from '@sentry/react';
import { UserUtils } from './utils';
import { SessionStorageUtils } from '@shared/utils';
import { useLoginStore } from '@lib/store';

const IGNORED_STATUS_ERRORS = [401];

export const baseURL = process.env.API_URL;

axios.defaults.headers.common.Accept = 'application/json';
axios.defaults.headers.common['Content-Type'] = 'application/json; charset=utf-8';

export class Api {
  private static readonly baseURL = process.env.API_URL;
  static _refreshPromise: Promise<void> | null = null;

  protected static get<Response = unknown>(request: IRequestParams<null>): Promise<AxiosResponse<Response>> {
    return this._wrapRequest(() =>
      this.callMethod({
        method: HttpMethod.Get,
        url: request.url,
        params: request.queryParams,
        baseURL: this.baseURL,
        signal: request.signal,
        responseType: request.responseType as AxiosResponse['data'],
      }),
    );
  }

  protected static post<Request, Response = unknown>(request: IRequestParams<Request>): Promise<AxiosResponse<Response>> {
    return this._wrapRequest(() =>
      this.callMethod({
        method: HttpMethod.Post,
        url: request.url,
        data: request.data,
        params: request.queryParams,
        baseURL: this.baseURL,
        signal: request.signal,
      }),
    );
  }

  protected static put<Request, Response = unknown>(request: IRequestParams<Request>): Promise<AxiosResponse<Response>> {
    return this._wrapRequest(() =>
      this.callMethod({
        method: HttpMethod.Put,
        url: request.url,
        data: request.data,
        params: request.queryParams,
        baseURL: this.baseURL,
        signal: request.signal,
      }),
    );
  }

  protected static delete<Request, Response = unknown>(request: IRequestParams<Request>): Promise<AxiosResponse<Response>> {
    return this._wrapRequest(() =>
      this.callMethod({
        method: HttpMethod.Delete,
        url: request.url,
        params: request.queryParams,
        data: request.data,
        baseURL: this.baseURL,
        signal: request.signal,
      }),
    );
  }

  protected static async plainPost<Request, Response = unknown>(request: IRequestParams<Request>): Promise<AxiosResponse<Response>> {
    const requestId = crypto.randomUUID();
    try {
      const response = await Axios({
        method: HttpMethod.Post,
        url: request.url,
        data: request.data,
        params: request.queryParams,
        baseURL: this.baseURL,
        signal: request.signal,
      });
      return response;
    } catch (error: any) {
      const status = error.response?.status;

      if (status === 401) {
        SessionStorageUtils.removeIcaState();
        useLoginStore.getState().clear();
        window.location.reload();

        return {
          status: 401,
          statusText: 'Unauthorized',
          data: {} as Response,
          headers: {},
          config: request,
        } as AxiosResponse<Response>;
      }

      const formatErrors = (errors: Record<string, string[]>) => {
        return Object.entries(errors)
          .map(([key, value]) => `${key}: ${value.join(', ')}`)
          .join('; ');
      };

      const errorMessage = error.response?.data?.title || (error.response?.data?.errors ? formatErrors(error.response.data.errors) : error.message);

      const errorApi = new RestApiError(errorMessage, error.response?.data?.status || status, error.response?.data, HttpMethod.Post, request.url!);

      setContext('Request', {
        requestId,
        method: HttpMethod.Post,
        url: request.url,
        status: status,
        message: errorMessage,
      });
      setUser({ id: UserUtils.getUserId() });
      captureException(errorApi);

      throw errorApi;
    }
  }

  private static async _wrapRequest<Response>(handler: () => Promise<Response>) {
    try {
      const result = await handler();
      return result;
    } catch (error) {
      if (RestApiError.isRestApiError(error) && IGNORED_STATUS_ERRORS.includes(error.status)) error.message = '';
      throw error;
    }
  }

  private static async callMethod<Response>(params: AxiosRequestConfig): Promise<AxiosResponse<Response>> {
    const requestId = crypto.randomUUID();
    try {
      const result = await Axios<Response>(params);
      return result;
    } catch (error: any) {
      const status = error.response?.status;

      if (status === 401) {
        SessionStorageUtils.removeIcaState();
        useLoginStore.getState().clear();
        window.location.reload();

        return {
          status: 401,
          statusText: 'Unauthorized',
          data: {} as Response,
          headers: {},
          config: params,
        } as AxiosResponse<Response>;
      }

      const formatErrors = (errors: Record<string, string[]>) => {
        return Object.entries(errors)
          .map(([key, value]) => `${key}: ${value.join(', ')}`)
          .join('; ');
      };

      const errorMessage = error.response?.data?.title || (error.response?.data?.errors ? formatErrors(error.response.data.errors) : error.message);

      const errorApi = new RestApiError(errorMessage, error.response?.data?.status || status, error.response?.data, params.method!, params.url!);

      setContext('Request', {
        requestId,
        method: params.method,
        url: params.url,
        status: status,
        message: errorMessage,
      });
      setUser({ id: UserUtils.getUserId() });
      captureException(errorApi);

      throw errorApi;
    }
  }
}
